import { config } from '../utils/config';
import { hasInteractAdminLicense } from '../utils/license';

/**
 * @typedef {Object} StatusCodeHandlers
 * @prop {number} statusCode
 * @prop {StatusCodeHandler} handler
 */

// Marks a request that already retried once after a 401 renewal, so a second consecutive 401
// (session genuinely gone) falls through instead of retrying forever.
export const RETRY_FLAG = '__retriedAfter401';

export const shouldRetryOnUnauthorized = (axiosError) =>
  axiosError?.response?.status === 401 && !axiosError?.config?.[RETRY_FLAG];

// Marks an idempotent request that already retried once after failing with no response.
export const NETWORK_RETRY_FLAG = '__retriedAfterNetworkError';

const IDEMPOTENT_METHODS = ['get', 'head', 'options'];

export const shouldRetryOnNetworkError = (axiosError) => {
  const config = axiosError?.config;
  const cancelled = axiosError?.code === 'ERR_CANCELED' || config?.signal?.aborted === true;
  return (
    !!config &&
    !axiosError.response &&
    !cancelled &&
    !config[NETWORK_RETRY_FLAG] &&
    IDEMPOTENT_METHODS.includes(String(config.method || 'get').toLowerCase())
  );
};

// Only a refusal from this app's own API can mean a missing licence; the check itself goes to
// CenterPoint, so it can never start another one. Concurrent refusals share one check, and a
// check that fails keeps the /unauthorized answer.
let pendingLicenseCheck = null;
const ownApiRefusedForLicense = (apiError) => {
  if (!apiError.config?.url?.startsWith(config.apiBaseUrl)) return Promise.resolve(false);
  if (!pendingLicenseCheck) {
    pendingLicenseCheck = hasInteractAdminLicense()
      .then((licensed) => !licensed, () => false)
      .finally(() => {
        pendingLicenseCheck = null;
      });
  }
  return pendingLicenseCheck;
};

/**
 * @param {*} apiError 
 * @param {StatusCodeHandlers[]} statusCodeHandlers 
 */
export const apiErrorHandler = async (apiError, statusCodeHandlers = null) => {
  if (!apiError.response) { // if the api didn't respond, then the regular axios error response will not be set
    throw apiError
  }
  const { status, data } = apiError.response
  const match = statusCodeHandlers?.find(s => s.statusCode === status)
  if (match) {
    match.handler()
  }
  else {
    if (status === 400) {
      if (data.isSafeMessage === true) {
        apiError.customErrorMessage = data.Message;
      }
      if (data.Message === 'Unable to save record. Record has been modified by another user.') {
        apiError.customErrorMessage = data.Message;
      }
      else if (data.ValidationErrors) {
        apiError.validationErrors = Object.keys(data.ValidationErrors).map((key) => {
          return {
            key,
            errors: data.ValidationErrors[key],
          }
        })
      }
    }
    else if (status === 404) {
      window.location.replace("/not-found")
    }
    else if (status === 403 && (await ownApiRefusedForLicense(apiError))) {
      window.location.replace("/missing-license")
    }
    else if (status === 401 || status === 403) {
      window.location.replace("/unauthorized")
    }
  }
  throw apiError
}